/**
 * "Repository map" (docs section 10): the top-level folders of the repo as a tree, with
 * the folders a developer works in one level down and one line on what each holds. The
 * seven files to open on the first day carry the tag "Open first". On a laptop the note
 * stands beside the name; on a phone it stands under it (the narrow media query).
 */

import { FileText, Folder, Icon } from "@norican/icons";
import { Badge, cn, Typography } from "@norican/react";
import type { ReactNode } from "react";

import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";

import { Figure } from "./figure";

export const REPO_MAP_TITLE = "Repository map";
export const REPO_MAP_OPEN_FIRST = "Open first";

/** What the drawing shows, for a screen reader. */
export const REPO_MAP_SUMMARY =
  "The folders of the repository as a tree, with the seven files to open first.";

/**
 * A row of the tree, named relative to its parent. `first` marks one of the seven files
 * to open on the first day; every other row is a folder and prints with a slash.
 */
interface Entry {
  name: string;
  /** One short line; backticks mark code names. */
  note: string;
  first?: true;
  children?: readonly Entry[];
}

export const REPO_TREE: readonly Entry[] = [
  { name: "AGENTS.md", note: "The rules for every change.", first: true },
  { name: "Makefile", note: "The commands: prepare, dev, lint, test, eval.", first: true },
  { name: ".mise.toml", note: "The pinned versions of the tools.", first: true },
  {
    name: "backend",
    note: "Python and FastAPI: the API, the mail-ingest job, the sync jobs.",
    children: [
      { name: "src/rfq_foundry/api/main.py", note: "Every endpoint of the API.", first: true },
      { name: "src/rfq_foundry/mail", note: "The mail-ingest job." },
      { name: "src/rfq_foundry/engine", note: "From a mail to a draft." },
      { name: "src/rfq_foundry/sync", note: "The sync jobs and the DDL of the mirror." },
      { name: "alembic/versions", note: "The migrations of the app DB." },
      { name: "tests", note: "The pytest tests and the guard tests." },
    ],
  },
  {
    name: "frontend",
    note: "Vite and React: the app in the browser.",
    children: [
      { name: "src/App.tsx", note: "Every page and its path.", first: true },
      { name: "src/features", note: "One folder for each page." },
      { name: "src/components/figures", note: "The drawings of these pages." },
      { name: "src/lib/api", note: "The hooks and the generated client." },
      { name: "tests", note: "The node tests." },
    ],
  },
  {
    name: "infra",
    note: "Azure: Terraform and scripts.",
    children: [
      { name: "main.tf", note: "The root module. It calls six modules.", first: true },
      { name: "modules", note: "One folder for each of the six modules." },
      { name: "scripts", note: "The scripts of the three sync jobs and of the alerts." },
    ],
  },
  {
    name: "openapi",
    note: "The contract between the backend and the frontend.",
    children: [{ name: "openapi.json", note: "The client is generated from it.", first: true }],
  },
  { name: ".github/workflows", note: "`deploy.yml`: a push to `dev` deploys." },
  { name: "docs", note: "The concept documents, as background." },
];

const BESIDE = "grid grid-cols-[minmax(0,22rem)_1fr] items-baseline gap-3";
const UNDER = "flex min-w-0 flex-col";

/** Backtick spans in code font, as the documentation page prints them. */
function code(text: string): ReactNode {
  return text.split("`").map((part, index) =>
    index % 2 ? (
      <Typography key={index} as="code" variant="code" className="text-[0.9em] break-words">
        {part}
      </Typography>
    ) : (
      part
    ),
  );
}

function Row({ entry, beside }: { entry: Entry; beside: boolean }) {
  const folder = !entry.first;
  return (
    <li className="flex min-w-0 flex-col gap-1.5">
      <span className={beside ? BESIDE : UNDER}>
        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          <Icon icon={folder ? Folder : FileText} size="sm" className="text-fg-muted shrink-0" />
          <Typography
            as="code"
            variant="code"
            className={cn("min-w-0 break-all", folder && "font-semibold")}
          >
            {entry.name}
            {folder && "/"}
          </Typography>
          {entry.first && (
            <Badge tone="accent" variant="subtle" size="sm">
              {REPO_MAP_OPEN_FIRST}
            </Badge>
          )}
        </span>
        <Typography variant="caption" tone="muted" className="break-words">
          {code(entry.note)}
        </Typography>
      </span>
      {entry.children && (
        <ul className="border-border-strong ml-2 flex flex-col gap-1.5 border-l pl-3">
          {entry.children.map((child) => (
            <Row key={child.name} entry={child} beside={beside} />
          ))}
        </ul>
      )}
    </li>
  );
}

/** `layout` overrides the media query for the render tests. Omit it in the app. */
export function RepoMapFigure(props: { caption?: string; layout?: "beside" | "under" }) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const form = props.layout ?? (narrow ? "under" : "beside");
  return (
    <Figure name="repo-map" title={REPO_MAP_TITLE} caption={props.caption}>
      <ul aria-label={REPO_MAP_SUMMARY} data-layout={form} className="flex flex-col gap-1.5">
        {REPO_TREE.map((entry) => (
          <Row key={entry.name} entry={entry} beside={form === "beside"} />
        ))}
      </ul>
    </Figure>
  );
}
